import {
  GameSettingType,
  SpeedometerType,
} from "@1games-ru/region-shared/dist/enums/account/settings";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import {
  imgPlayerMenuSpeedometerClassic,
  imgPlayerMenuSpeedometerMaximal,
  imgPlayerMenuSpeedometerMinimal,
} from "@/shared/assets/img";
import { Select, SelectOption, Slider, Switcher } from "@/shared/ui";
import getTranslationSettingString from "@/domains/playerMenu/helpers/getTranslationSettingString";
import PlayerMenuControlLabel from "@/domains/playerMenu/components/PlayerMenuControlLabel";
import PlayerMenuTooltip from "@/domains/playerMenu/components/PlayerMenuTooltip";
import useBasicValues from "@/domains/settings/basic/hooks/useBasicValues";
import { appliedBasicSettingsSelector, setAppliedSettings } from "@/domains/settings/basic/slice";

const speedometerBg = `linear-gradient(135deg, #2c2416 0%, #3a2e1a 25%, #4a3b20 50%, #3a2e1a 75%, #2c2416 100%),
  radial-gradient(circle, transparent 20%, #2c2416 20%, #2c2416 80%, transparent 80%) 0 0 / 40px 40px`;

const speedometerImgs: { value: SpeedometerType; src: string }[] = [
  { value: SpeedometerType.Minimal, src: imgPlayerMenuSpeedometerMinimal },
  { value: SpeedometerType.Classic, src: imgPlayerMenuSpeedometerClassic },
  { value: SpeedometerType.Maximal, src: imgPlayerMenuSpeedometerMaximal },
];

const mainMenuTransparencyValues = [
  { label: "0", value: 0 },
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
  { label: "40", value: 40 },
  { label: "50", value: 50 },
  { label: "60", value: 60 },
  { label: "70", value: 70 },
  { label: "80", value: 80 },
  { label: "90", value: 90 },
  { label: "100", value: 100 },
];

export default function PlayerMenuSettingsBasicHudContainer() {
  const { t } = useTranslation("playerMenu");
  const appliedBasicSettings = useAppSelector(appliedBasicSettingsSelector);
  const dispatch = useAppDispatch();
  const { speedometerTypeValues } = useBasicValues();

  return (
    <div className="grid grid-cols-2 gap-5.5">
      <div>
        <div>
          <div className="flex items-center justify-between">
            <PlayerMenuControlLabel>
              {t(getTranslationSettingString(GameSettingType.SettingSpeedometerStyle))}
            </PlayerMenuControlLabel>
            <PlayerMenuTooltip text="Изменение внешнего вида спидометра" />
          </div>
          <Select
            value={t(
              `playerMenu.speedometerStyle.${SpeedometerType[appliedBasicSettings[GameSettingType.SettingSpeedometerStyle]]}`,
            )}
            bg="bg-dark-800"
            className="mt-3"
          >
            {speedometerTypeValues.map((item) => (
              <SelectOption
                key={item}
                onClick={() =>
                  dispatch(
                    setAppliedSettings({
                      [GameSettingType.SettingSpeedometerStyle]: item,
                    }),
                  )
                }
                isSelected={item === appliedBasicSettings[GameSettingType.SettingSpeedometerStyle]}
                className="hover:text-accent-solid transition-all"
              >
                {t(`playerMenu.speedometerStyle.${SpeedometerType[item]}`)}
              </SelectOption>
            ))}
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-2 p-4 mt-1.5 bg-dark-800 rounded-[0.625rem]">
          {speedometerImgs.map((item) => (
            <button
              type="button"
              key={item.value}
              className={clsx(
                "flex aspect-square rounded-[0.5rem] p-0.5 border-2 transition-all",
                appliedBasicSettings[GameSettingType.SettingSpeedometerStyle] === item.value
                  ? "border-accent-solid"
                  : "border-transparent",
              )}
              onClick={() =>
                dispatch(
                  setAppliedSettings({
                    [GameSettingType.SettingSpeedometerStyle]: item.value,
                  }),
                )
              }
            >
              <div
                className="h-full w-full flex items-center justify-center rounded-[0.325rem]"
                style={{ background: speedometerBg }}
              >
                <img src={item.src} alt="" className="w-[80%]" />
              </div>
            </button>
          ))}
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <PlayerMenuControlLabel>
              {t(getTranslationSettingString(GameSettingType.SettingMainMenuTransparency))}
            </PlayerMenuControlLabel>
          </div>
          <Slider
            value={appliedBasicSettings[GameSettingType.SettingMainMenuTransparency]}
            values={mainMenuTransparencyValues}
            min={0}
            max={100}
            step={1}
            onChange={(value) =>
              dispatch(
                setAppliedSettings({
                  [GameSettingType.SettingMainMenuTransparency]: value,
                }),
              )
            }
            className="mt-1"
            classNameSlider="mt-3"
            classNameLabel="text-12 font-bold"
          />
        </div>
      </div>
      <div className="w-full h-fit flex flex-col rounded-[0.625rem] bg-dark-800 border border-dark-700/20">
        <div className="h-[3.125rem] px-3.5 flex items-center justify-between">
          <div className="text-14 font-bold">
            {t(getTranslationSettingString(GameSettingType.SettingShowHotkeys))}
          </div>
          <Switcher
            on={appliedBasicSettings[GameSettingType.SettingShowHotkeys]}
            onChange={() =>
              dispatch(
                setAppliedSettings({
                  [GameSettingType.SettingShowHotkeys]:
                    !appliedBasicSettings[GameSettingType.SettingShowHotkeys],
                }),
              )
            }
            withBorder
          />
        </div>
        <div className="h-[3.125rem] px-3.5 flex items-center justify-between border-t border-dark-700/20">
          <div className="text-14 font-bold">
            {t(getTranslationSettingString(GameSettingType.SettingShowChat))}
          </div>
          <Switcher
            on={appliedBasicSettings[GameSettingType.SettingShowChat]}
            onChange={() =>
              dispatch(
                setAppliedSettings({
                  [GameSettingType.SettingShowChat]:
                    !appliedBasicSettings[GameSettingType.SettingShowChat],
                }),
              )
            }
            withBorder
          />
        </div>
        <div className="h-[3.125rem] px-3.5 flex items-center justify-between border-t border-dark-700/20">
          <div className="text-14 font-bold">
            {t(getTranslationSettingString(GameSettingType.SettingShowMap))}
          </div>
          <Switcher
            on={appliedBasicSettings[GameSettingType.SettingShowMap]}
            onChange={() =>
              dispatch(
                setAppliedSettings({
                  [GameSettingType.SettingShowMap]:
                    !appliedBasicSettings[GameSettingType.SettingShowMap],
                }),
              )
            }
            withBorder
          />
        </div>
        <div className="h-[3.125rem] px-3.5 flex items-center justify-between border-t border-dark-700/20">
          <div className="text-14 font-bold">
            {t(getTranslationSettingString(GameSettingType.SettingShowHUD))}
          </div>
          <Switcher
            on={appliedBasicSettings[GameSettingType.SettingShowHUD]}
            onChange={() => {
              const newShowHud = !appliedBasicSettings[GameSettingType.SettingShowHUD];

              if (!newShowHud) {
                dispatch(
                  setAppliedSettings({
                    [GameSettingType.SettingShowChat]: false,
                    [GameSettingType.SettingShowHotkeys]: false,
                    [GameSettingType.SettingShowMap]: false,
                  }),
                );
              }
              dispatch(setAppliedSettings({ [GameSettingType.SettingShowHUD]: newShowHud }));
            }}
            withBorder
          />
        </div>
      </div>
    </div>
  );
}
